import SelectComponent from "@via/components/select/select";
import {
  useAppsList,
  useLaunchApplication,
} from "@via/pages/apps/apps.api.tsx";
import { SelectOption } from "@via/utils/interfaces";
import {
  AppListResponseBodyItem,
  AppStatus,
  AppViewType,
  CallReportAppRequestBody,
  RunReportResource,
  StartAppType,
} from "common";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { SuggestedApp } from "@via/pages/projects/runs/reports/components/suggested-app";

import { Spinner } from "@via/components/loading/loading.tsx";
import { getDirectoryName } from "@via/pages/projects/runs/reports/utils/helpers.ts";
import { useDebounceCallback, useInterval } from "usehooks-ts";
import { useRunAppStatus } from "@via/pages/projects/runs/runs.api";

export function AppReport({
  runUUID,
  report,
  reportTree,
}: {
  runUUID: string;
  report: RunReportResource;
  reportTree: RunReportResource[];
}): JSX.Element {
  const [selectedDir, setSelectedDir] = useState<string>("");
  const { runId = "" } = useParams();
  const [selectedAppId, setSelectedAppId] =
    useState<SelectOption["value"]>(null);
  const [accessUrlInIframe, setAccessUrlInIframe] = useState<string>("");
  const [searchedInput, setSearchedInput] = useState("");
  const [cumulativeApps, setCumulativeApps] = useState<
    AppListResponseBodyItem[]
  >([]);
  const [appsPage, setAppsPage] = useState(0);
  const appOptionSize = 10;

  const { data: appsListData, isLoading: isAppsListLoading } = useAppsList(
    appsPage,
    appOptionSize,
    [],
    [
      { id: "name", value: searchedInput },
      { id: "type", value: AppViewType.NonAdminDefault },
    ]
  );
  const {
    mutate: launchAppInstance,
    data: launchAppData,
    isPending: isLaunchAppLoading,
    isSuccess: isLaunchAppSuccess,
    isError: isLaunchAppError,
  } = useLaunchApplication();

  const fetchMoreApps = useCallback(() => {
    setAppsPage(currentPage => {
      const nextPage = currentPage + 1;
      const maxPage = Math.ceil((appsListData?.total ?? 0) / appOptionSize) - 1;
      return nextPage <= maxPage ? nextPage : currentPage;
    });
  }, [appsListData?.total]);

  const debouncedHandleInputChange = useDebounceCallback((value: string) => {
    if (value === searchedInput) return;
    setSearchedInput(value);
  }, 300);

  const { data: runAppStatus, refetch: refetchRunAppStatus } =
    useRunAppStatus(runUUID);

  const selectedRunAppStatus = useMemo(() => {
    return runAppStatus?.find(
      ({ filename, dir }) => filename === report.name && dir === selectedDir
    );
  }, [runAppStatus, report.name, selectedDir]);

  useEffect(() => {
    if (selectedRunAppStatus?.status === AppStatus.Running) {
      if (!selectedRunAppStatus.blankLaunch) {
        setAccessUrlInIframe(selectedRunAppStatus.accessUrl);
      }
    } else {
      setAccessUrlInIframe("");
    }
  }, [selectedRunAppStatus, appsListData]);

  useEffect(() => {
    setSelectedDir(getDirectoryName(reportTree, "", report.id));
  }, [report.id]);

  useEffect(() => {
    if (isLaunchAppSuccess || isLaunchAppError) {
      refetchRunAppStatus();
    }
  }, [refetchRunAppStatus, isLaunchAppSuccess, isLaunchAppError]);

  useEffect(() => {
    if (isLaunchAppSuccess && launchAppData) {
      const { accessUrl, blankLaunch } = launchAppData;
      if (selectedRunAppStatus?.status === AppStatus.Running) {
        if (blankLaunch) {
          window.open(accessUrl, "_blank", "noreferrer");
        } else {
          setAccessUrlInIframe(accessUrl);
        }
      }
    }
  }, [isLaunchAppSuccess, launchAppData, appsListData, selectedRunAppStatus]);

  useInterval(
    () => {
      refetchRunAppStatus();
    },
    selectedRunAppStatus?.status === AppStatus.Running ||
      selectedRunAppStatus?.status === AppStatus.Creating
      ? 15000
      : null
  );

  useEffect(() => {
    if (appsListData?.data) {
      setCumulativeApps(prevApps => {
        // Append new results while preventing duplicates
        const existingIds = new Set(prevApps.map(p => p.id));
        const newApps = appsListData?.data?.filter(p => !existingIds.has(p.id));

        return [...prevApps, ...newApps];
      });
    }
  }, [appsListData?.data, searchedInput, appsPage]);

  const appListOptions = useMemo(() => {
    return (
      cumulativeApps
        .map(app => {
          if (app.revisions) {
            return app.revisions.map(revision => ({
              label: revision.name,
              value: revision.id,
              imageContainer: revision.image || "",
              version: revision.version || "",
            }));
          } else {
            return {
              label: app.name,
              value: app.id,
              imageContainer: app.image || "",
              version: app.version || "",
            };
          }
        })
        ?.flat() ?? []
    );
  }, [cumulativeApps]);

  const prepareRequest = (): CallReportAppRequestBody => {
    return {
      dir: selectedDir,
      runUUID,
      filename: report.name.split(".app")[0], // for now, remove .app because we mocked the data
      projectPipelineId: Number(runId),
      type: StartAppType.Report,
    };
  };

  const handleLaunchApp = (suggestedAppId?: number) => {
    launchAppInstance({
      id: suggestedAppId || selectedAppId,
      payload: prepareRequest(),
    });
  };

  const renderRunAppStatus = (): string => {
    if (isLaunchAppLoading) {
      return "Initializing..";
    }
    switch (selectedRunAppStatus?.status) {
      case AppStatus.Initiated:
        return "Initiating..";
      case AppStatus.Downloading:
        return "Downloading..";
      case AppStatus.Pending:
        return "Pending..";
      case AppStatus.Running:
        return "Running..";
      case AppStatus.Creating:
        return "Creating..";
      case AppStatus.Terminated:
        return "Terminated.";
      default:
        return "Ready to Launch..";
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex gap-1">
        <div className="font-semibold">App Status:</div>
        <div>{renderRunAppStatus()}</div>
        {selectedRunAppStatus?.appName && (
          <div className="italic text-gray-500">
            ({selectedRunAppStatus.appName})
          </div>
        )}
      </div>
      {accessUrlInIframe ? (
        <iframe className="h-[78dvh] w-full" src={accessUrlInIframe} />
      ) : (
        (!selectedRunAppStatus?.status ||
          selectedRunAppStatus?.status === AppStatus.Terminated) && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
              {report?.apps &&
                report?.apps?.map(app => (
                  <SuggestedApp
                    isLaunchAppLoading={isLaunchAppLoading}
                    key={app.id}
                    app={app}
                    handleLaunchApp={suggestedAppId => {
                      setSelectedAppId(suggestedAppId);
                      handleLaunchApp(suggestedAppId);
                    }}
                  />
                ))}
            </div>
            <div className="min-w-[300px]">
              <SelectComponent
                placeholder="Select an App"
                name="app"
                isLoading={isAppsListLoading || isLaunchAppLoading}
                options={appListOptions}
                onChange={selected => {
                  setSelectedAppId((selected as SelectOption)?.value || null);
                }}
                onFetchMoreOptions={fetchMoreApps}
                onInputChange={inputValue =>
                  debouncedHandleInputChange(inputValue)
                }
                formatOptionLabel={(option: any) => (
                  <>
                    <div>
                      {option.label}({option.version})
                    </div>
                    <div className="text-xs text-gray-500">
                      {option.imageContainer}
                    </div>
                  </>
                )}
              />
            </div>
            <div className="mb-4 flex gap-3 text-gray-600">
              <button
                disabled={selectedAppId === null || isLaunchAppLoading}
                className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                onClick={() => handleLaunchApp()}>
                <Spinner loading={isLaunchAppLoading}>Launch App</Spinner>
              </button>
            </div>
          </>
        )
      )}
    </div>
  );
}
